import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {PortalPrivacyNotice} from '../../portal_usage/PortalPrivacyNotice';
import {PortalUsageEmptyState} from '../../portal_usage/PortalUsageEmptyState';
import {normalizeTurns, TURN_KIND, TURNS_PAGE_SIZE, turnKey} from '../../portal_usage/portalUsageState';
import {PortalUsageTurn} from './PortalUsageTurn';

/**
 * Las últimas consultas del cliente a su asistente, una por turno.
 *
 * Sólo las del CLIENTE: el backend no manda las del equipo de la agencia acá, y está bien que sea
 * así — la pregunta que contesta esta sección es qué necesita el cliente, y el reparto entre los
 * dos actores ya lo cuenta el bloque "Quién consulta" de arriba.
 *
 * "Ver más" revela turnos que YA están en memoria: el backend manda todos los que viajan en una
 * sola respuesta, sin paginación. Así que acá no puede aparecer el clásico bug del load-more
 * paginado (filas duplicadas en el borde entre páginas, respuestas viejas pisando estado nuevo).
 *
 * La visibilidad del texto la decide el SERVIDOR (`query_text_visible`), nunca esta pantalla. Y se
 * USA: es lo que permite decir «no te lo mostramos» en vez de dejar al usuario adivinando si el
 * cliente no escribió nada.
 */
export function PortalQuestionsFeed({usage, queryTextVisible}) {
    const {t} = useTranslation();
    const [visibleCount, setVisibleCount] = useState(TURNS_PAGE_SIZE);

    const turns = normalizeTurns(usage);
    if (!turns.length) return null;

    const shown = turns.slice(0, visibleCount);
    const hasAnyQuestion = countQueries(turns) > 0;
    const sample = (usage && usage.interactions_sample) || {};

    return (
        <div className="box pu-box">
            <div className="pu-box__head">
                <h3 className="pu-box__title">{t('portal_usage.questions_title', 'Preguntas del cliente')}</h3>
            </div>

            {queryTextVisible
                ? <PortalPrivacyNotice variant="agency"/>
                : (
                    <div className="pu-notice -info">
                        <p>{t('portal_usage.text_policy_off',
                            'El texto de las preguntas está desactivado para esta agencia. Ves cuándo '
                            + 'consultó, sobre qué y cuánto tardó, pero no lo que escribió.')}</p>
                    </div>
                )}

            {/* El vacío va DENTRO de la caja, no en lugar de ella: si el bloque desaparece, la
                agencia no sabe que existe y no puede verificar nada. */}
            {hasAnyQuestion ? null : (
                <PortalUsageEmptyState
                    kind="NO_QUESTIONS"
                    count={turns.reduce((total, turn) => total + (turn.steps_count || 0), 0)}
                />
            )}

            <ul className="pu-turns">
                {shown.map((turn, index) => (
                    <PortalUsageTurn
                        key={turnKey(turn, index)}
                        turn={turn}
                        textVisible={queryTextVisible}
                    />
                ))}
            </ul>

            {/* El único `aria-live` de la sección: sin él, un usuario de teclado aprieta «Ver más»
                y no recibe ninguna señal de que aparecieron filas. En la lista sería peor —25 filas
                leídas en voz alta— y en el toggle sobra, porque `aria-expanded` ya lo anuncia. */}
            <p className="pu-turns__status" aria-live="polite">
                {/* Cuenta CONSULTAS, no filas: los turnos de sólo contexto por definición no lo
                    son, y decir «8 de 8 consultas» justo debajo del vacío que anuncia que no hubo
                    ninguna es la pantalla contradiciéndose a sí misma. */}
                {t('portal_usage.turns_status', 'Mostrando {{shown}} de {{total}} consultas recientes',
                    {shown: countQueries(shown), total: countQueries(turns)})}
                {sample.limited
                    ? ` · ${t('portal_usage.turns_sample', 'sobre sus últimas {{n}} interacciones',
                        {n: sample.rows_scanned})}`
                    : ''}
            </p>

            {turns.length > shown.length ? (
                <div className="pu-turns__more">
                    <button
                        className="btn -alt -negative"
                        onClick={() => setVisibleCount(count => count + TURNS_PAGE_SIZE)}
                    >
                        {t('portal_usage.questions_more', 'Ver más consultas')}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

/** Cuántos de estos turnos son una consulta de verdad (los de sólo contexto no lo son). */
function countQueries(turns) {
    return (turns || []).filter(turn => turn.kind !== TURN_KIND.CONTEXT).length;
}

export default PortalQuestionsFeed;
